/**
 * fold — feed rows → the folded item rows every selector reads.
 *
 * The feed is an append-only event log: `GET /widget-instances/{id}/data`
 * returns `[{ id, kind, payload, source_session_id, log_card_id, created_at }]`
 * newest-first. Folding collapses that log to the CURRENT items, per the
 * blueprint's `data.fold` block:
 *
 *   key / fallback_key   identity (medicine-box: payload.name · wardrobe:
 *                        payload.item_id → row id · nutritionist: row id)
 *   strategy             ONE of FOLD_STRATEGIES (below) — how N events for one
 *                        identity become one item
 *   correction_key       a row carrying this key EDITS its target in place and
 *                        is never a second visible row (nutritionist `edits`)
 *   drop_on              discriminator values that DELETE the target
 *                        (`remove`)
 *   order                an explicit sort ("finished_at desc"), for a feed whose
 *                        display order is NOT its write order
 *
 * THE STRATEGY ENUM (README §4). Each one is still a pure recompute over
 * `entries[]` — a fold never remembers anything between feeds:
 *
 *   latest-wins           the newest row for an identity REPLACES the older one
 *                         wholesale. (my-wardrobe)
 *   event-sourced-latest  the newest row MERGES over the older one, field by
 *                         field, so a partial correction never blanks a known
 *                         field. (medicine-box, nutritionist — the default)
 *   field-merge           event-sourced with a BASE: the last create-ish event
 *                         is the row, every later event overlays only the
 *                         fields it carries (optionally only `editable_fields`),
 *                         a tombstone drops the identity ONLY when it is the
 *                         newest event, and other KINDS fold in as named child
 *                         streams. `entry_id` stays the base row's id.
 *   group-into-one        many rows → ONE card that still owns all of them:
 *                         group by `key` (fallback identity `legacy:<id>`),
 *                         pick a REPRESENTATIVE, keep `entries[]` so a
 *                         downstream filter can re-pick it.
 *   none                  no merge at all — identity is the row, and `order`
 *                         decides the sequence. (a feed the agent PATCHes in
 *                         place, so the newest snapshot of a row is the row)
 *
 * Sealed behaviours implemented here once for the whole kit: `edit`/`remove`
 * event rows never render as items, `is_sample:true` seed rows disappear as
 * soon as a real row exists, and identity survives an edit.
 */
import { getPath } from './paths';

/** The closed strategy set a blueprint's `data.fold.strategy` may name. */
export const FOLD_STRATEGIES = [
  'latest-wins',
  'event-sourced-latest',
  'field-merge',
  'group-into-one',
  'none',
];

/** The strategy an absent/unknown one degrades to (the historical default). */
const DEFAULT_STRATEGY = 'event-sourced-latest';

/**
 * Fields that are the ROW's, never the payload's: identity, provenance and the
 * event discriminator. A later event may not overwrite them by merging — an
 * `edit` row copying its own `type` onto its target would turn a garment into
 * an "edit" and drop it out of every selector, and copying its `created_at`
 * would make an edited row claim it was created when it was edited.
 */
const META_KEYS = new Set([
  'entry_id', 'id', 'kind', 'created_at', 'source_session_id', 'log_card_id',
  'session_deleted', 'has_session', 'type', 'op', 'action',
]);

/** Row-level discriminator: the wardrobe's `type`, or a generic op/action. */
function discriminatorOf(payload) {
  return payload?.type ?? payload?.op ?? payload?.action ?? null;
}

/**
 * Read a fold key off a NORMALIZED row. A blueprint may spell it either way —
 * `"plant_id"` (post-normalization, how the runtime sees it) or
 * `"payload.plant_id"` (how the feed writes it) — and both must find the same
 * field, because the two spellings appear in the same design docs.
 */
function readKey(row, key) {
  if (!key) return undefined;
  const path = String(key).startsWith('payload.') ? String(key).slice('payload.'.length) : String(key);
  return path.includes('.') || path.includes('[') ? getPath(row, path) : row?.[path];
}

/**
 * Flatten one feed row into the shape binds read: payload fields at the top
 * level plus the row metadata the kit needs for identity, media and the
 * session affordances.
 */
export function normalizeEntry(entry) {
  const payload = (entry && entry.payload) || {};
  const images = Array.isArray(payload.images)
    ? payload.images.filter(Boolean)
    : (payload.image_url ? [payload.image_url] : []);
  const sessionId = entry?.source_session_id || payload.source_session_id || null;
  return {
    ...payload,
    entry_id: entry?.id ?? payload.id ?? null,
    id: payload.id ?? entry?.id ?? null,
    kind: entry?.kind ?? payload.kind ?? null,
    source_session_id: sessionId,
    log_card_id: entry?.log_card_id || payload.log_card_id || null,
    created_at: entry?.created_at || payload.created_at || null,
    session_deleted: Boolean(entry?.session_deleted),
    has_session: Boolean(sessionId) && !entry?.session_deleted,
    name: payload.name || payload.title || payload.label || '',
    image_url: payload.image_url || images[0] || '',
    images,
    // Job-by-Contract (S3 + V3e) — the contract fields are TOP-LEVEL on the DTO
    // (`_mark_contract_regions`), not inside `payload`, so the `...payload` spread never
    // carried them. Carry them through the fold so a folded row — and the detail page's
    // `focusEntry`, always a folded row — keeps `region` / `contract_lifecycle` /
    // `contract_progress` (the card badge + the progress card read these). Only-when-present
    // so a non-contract row is byte-identical (BC). Flutter twin: `viewIrFlattenEntry`.
    ...(entry?.region != null ? { region: entry.region } : null),
    ...(entry?.contract_uid != null ? { contract_uid: entry.contract_uid } : null),
    ...(entry?.contract_lifecycle != null ? { contract_lifecycle: entry.contract_lifecycle } : null),
    ...(entry?.contract_progress != null ? { contract_progress: entry.contract_progress } : null),
  };
}

/** Milliseconds for a row's write time (0 when unparseable — oldest). */
function timeOf(row, field = 'created_at') {
  return Date.parse(getPath(row, field) || '') || 0;
}

/** Oldest-first — folding must replay the log in write order. */
function chronological(rows) {
  return [...rows].sort((a, b) => timeOf(a) - timeOf(b));
}

/**
 * `"finished_at desc"` → `{ field, dir }`. An `order` with no direction sorts
 * DESC, because every feed in this kit reads newest-first.
 */
function parseOrder(order) {
  if (!order || typeof order !== 'string') return null;
  const [field, dir] = order.trim().split(/\s+/);
  if (!field) return null;
  return { field, dir: String(dir || 'desc').toLowerCase() === 'asc' ? 'asc' : 'desc' };
}

/**
 * Apply an explicit `order`, with `fallback_order_key` standing in wherever the
 * primary field is absent — `finished_at || created_at` is the shape a notes
 * feed needs, and an unsorted tail of undated rows is not an ordering.
 */
function applyOrder(items, foldSpec) {
  const ord = parseOrder(foldSpec.order);
  if (!ord) return items;
  const fallback = foldSpec.fallback_order_key || 'created_at';
  const at = (r) => String(getPath(r, ord.field) ?? getPath(r, fallback) ?? '');
  const sorted = [...items].sort((a, b) => at(a).localeCompare(at(b)));
  return ord.dir === 'asc' ? sorted : sorted.reverse();
}

/**
 * "This value carries nothing, so it may not overwrite something."
 *
 * THE EMPTY ARRAY IS THE THIRD EMPTY, and it is the one that bit: every event
 * goes through `normalizeEntry`, which SYNTHESIZES `images: []` on any row that
 * carries no picture. A correction event — `{plant_id, nickname}`, a
 * nutritionist `edits` row, a medicine-box retitle — therefore arrives at the
 * overlay carrying a perfectly real, perfectly empty `images[]`, and a merge
 * that only guards null/'' writes it straight over the target's photos. The
 * whole point of an event-sourced fold is that a partial update never blanks a
 * known field; `[]` is exactly as partial as `null`.
 *
 * A row that genuinely means "remove all the pictures" is a `remove`/`edit`
 * event with its own discriminator, not an accidental `[]`.
 */
function isBlank(v) {
  return v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length);
}

/** Drop the blanks so a partial update never blanks a known field. */
function stripEmpty(row) {
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    if (isBlank(v)) continue;
    out[k] = v;
  }
  return out;
}

/** Demo seed rows vanish the moment the user owns a real one. */
function dropSamples(items) {
  return items.some((r) => !r.is_sample) ? items.filter((r) => !r.is_sample) : items;
}

/**
 * `latest-wins` / `event-sourced-latest` — the original fold, unchanged.
 *
 * Returns newest-touch-first, which is the order every grid renders in (the
 * newest capture lands in the first slot).
 */
function foldLatest(rows, foldSpec, merge) {
  const key = foldSpec.key || 'id';
  const fallbackKey = foldSpec.fallback_key || 'entry_id';
  const correctionKey = foldSpec.correction_key || null;
  const dropOn = new Set(foldSpec.drop_on || []);

  const ordered = chronological(rows);
  // identity → { row, seq }. `seq` is the write index of the newest touch,
  // so an edited row keeps its identity but floats to the top of the grid.
  const byId = new Map();
  // A correction/`edit` row addresses its target by id — the target may be
  // keyed by `key` OR by its row id, so index both.
  const aliases = new Map();

  const identify = (row) => {
    const k = readKey(row, key) ?? readKey(row, fallbackKey) ?? row.entry_id;
    return k == null ? null : String(k);
  };

  ordered.forEach((row, seq) => {
    const disc = discriminatorOf(row);

    // 1. deletes — drop the target and its aliases.
    if (disc != null && dropOn.has(String(disc))) {
      const target = String(readKey(row, key) ?? row.item_id ?? row.target_id ?? row.id ?? '');
      const id = aliases.get(target) || target;
      byId.delete(id);
      return;
    }

    // 2. corrections — nutritionist `payload.edits = <target entry id>` and the
    //    wardrobe's `type:'edit'`. Overwrite the target's changed fields IN
    //    PLACE; never a second row.
    const correctionTarget = correctionKey ? row[correctionKey] : null;
    const isEdit = disc === 'edit';
    if (correctionTarget || isEdit) {
      const rawTarget = String(correctionTarget || readKey(row, key) || row.item_id || row.target_id || '');
      const id = aliases.get(rawTarget) || rawTarget;
      const prev = byId.get(id);
      if (prev) {
        const patch = { ...row };
        // Identity, provenance and the DISCRIMINATOR stay the target's own: an
        // `edit` row is an event, so copying its `type` onto the target would
        // turn a garment into an "edit" and drop it out of every selector.
        delete patch.entry_id;
        delete patch.id;
        delete patch.kind;
        if (isEdit) delete patch.type;
        if (correctionKey) delete patch[correctionKey];
        for (const [k, v] of Object.entries(patch)) {
          if (isBlank(v)) continue;
          prev.row[k] = v;
        }
        prev.row.edited = true;
        prev.seq = seq;
      }
      return;
    }

    // 3. an ordinary item row.
    const id = identify(row);
    if (id == null) return;
    aliases.set(id, id);
    if (row.entry_id != null) aliases.set(String(row.entry_id), id);
    if (row.item_id != null) aliases.set(String(row.item_id), id);
    const prev = byId.get(id);
    if (prev && merge) {
      byId.set(id, { row: { ...prev.row, ...stripEmpty(row) }, seq });
    } else {
      byId.set(id, { row: { ...row }, seq });
    }
  });

  return [...byId.values()].sort((a, b) => b.seq - a.seq).map((v) => v.row);
}

/**
 * `field-merge` — the EVENT-SOURCED fold with a base row.
 *
 * The shape three widgets already fold by hand, generalised:
 *   base        the LAST create-ish event (`base_on`, default `type:"create"`);
 *               a stream with no discriminator at all uses its FIRST row, which
 *               is what makes a plain "corrections append" feed work unchanged.
 *   overlay     every later base-stream event writes ONLY the fields it carries
 *               (non-null), restricted to `editable_fields` when declared —
 *               everything else always comes from the base.
 *   tombstone   a `drop_on` event drops the identity ONLY WHEN IT IS THE NEWEST
 *               event for it: a `remove` followed by anything later is UNDONE,
 *               which is why it is a max-timestamp comparison and not a flag.
 *               `drop_when` is the other spelling — a merged FIELD that reads
 *               true (a chat-side `removed: true` correction).
 *   children    other KINDS fold in as named ordered sub-collections on the row
 *               (`children: {checks: "health_check", cares: "care_log"}`), so a
 *               detail page can bind `focusEntry.checks` without a second fold.
 *
 * IDENTITY IS DELIBERATELY TWO FIELDS, because the fleet needs both:
 *   `entry_id`         the BASE event's row id — what an item page opens.
 *   `latest_entry_id`  the NEWEST base event's row id — the delete/attach
 *                      anchor the host long-press and `pending.updates` key on.
 */
function foldFieldMerge(rows, foldSpec) {
  const key = foldSpec.key || 'id';
  const fallbackKey = foldSpec.fallback_key || 'entry_id';
  const baseOn = new Set((foldSpec.base_on || ['create']).map(String));
  const dropOn = new Set((foldSpec.drop_on || []).map(String));
  const dropWhen = foldSpec.drop_when || null;
  const editable = Array.isArray(foldSpec.editable_fields) && foldSpec.editable_fields.length
    ? new Set(foldSpec.editable_fields)
    : null;
  const childMap = new Map(Object.entries(foldSpec.children || {}).map(([name, kind]) => [String(kind), name]));
  const childOrder = foldSpec.child_order || 'created_at';
  const fromKinds = foldSpec.from_kind
    ? new Set([String(foldSpec.from_kind)])
    : (Array.isArray(foldSpec.from_kinds) ? new Set(foldSpec.from_kinds.map(String)) : null);

  // 1. bucket every row (base stream AND child streams) by identity.
  const buckets = new Map();
  for (const row of chronological(rows)) {
    const raw = readKey(row, key) ?? readKey(row, fallbackKey);
    if (raw == null || raw === '') continue;
    const id = String(raw);
    if (!buckets.has(id)) buckets.set(id, { base: [], children: new Map(), newest: 0 });
    const bucket = buckets.get(id);
    bucket.newest = Math.max(bucket.newest, timeOf(row));
    const childName = childMap.get(String(row.kind));
    if (childName) {
      if (!bucket.children.has(childName)) bucket.children.set(childName, []);
      bucket.children.get(childName).push(row);
      continue;
    }
    if (fromKinds && row.kind != null && !fromKinds.has(String(row.kind))) continue;
    bucket.base.push(row);
  }

  const out = [];
  for (const [id, bucket] of buckets) {
    const base = bucket.base;
    // No base event at all ⇒ ORPHAN. A child stream with nothing to hang on
    // is not an item (art-studio drops a piece whose create never landed).
    if (!base.length) continue;

    // 2. the tombstone, resolved by RECENCY rather than by presence.
    const removes = base.filter((r) => dropOn.has(String(discriminatorOf(r))));
    if (removes.length) {
      const lastRemove = Math.max(...removes.map((r) => timeOf(r)));
      const others = [...base.filter((r) => !dropOn.has(String(discriminatorOf(r)))),
        ...[...bucket.children.values()].flat()];
      const lastOther = others.length ? Math.max(...others.map((r) => timeOf(r))) : -1;
      if (lastRemove >= lastOther) continue;
    }

    const live = base.filter((r) => !dropOn.has(String(discriminatorOf(r))));
    if (!live.length) continue;

    // The base: the LAST create-ish event, or — for a stream that carries no
    // discriminator at all (a plain "corrections append" feed) — its first
    // plain row. A stream that DOES name its event types and never names a
    // create is an ORPHAN: an edit with nothing to edit is not an item.
    const creates = live.filter((r) => baseOn.has(String(discriminatorOf(r))));
    const anchor = creates.length
      ? creates[creates.length - 1]
      : live.find((r) => discriminatorOf(r) == null);
    if (!anchor) continue;
    const anchorAt = live.indexOf(anchor);
    const newest = live[live.length - 1];

    // 3. overlay every later event's non-null fields onto the base.
    const merged = { ...anchor };
    for (const row of live.slice(anchorAt + 1)) {
      const isCreate = baseOn.has(String(discriminatorOf(row)));
      for (const [k, v] of Object.entries(row)) {
        if (META_KEYS.has(k)) continue;
        if (isBlank(v)) continue;
        // `editable_fields` scopes the OVERLAY events only — a later create is
        // a full row, not a patch.
        if (editable && !isCreate && !editable.has(k)) continue;
        merged[k] = v;
      }
    }
    if (dropWhen && merged[dropWhen] === true) continue;

    // 4. the named child streams, oldest-first (a timeline reads forward).
    for (const [name, list] of bucket.children) {
      merged[name] = [...list].sort((a, b) => {
        const d = timeOf(a, childOrder) - timeOf(b, childOrder);
        return d !== 0 ? d : timeOf(a) - timeOf(b);
      });
    }
    for (const name of childMap.values()) if (!merged[name]) merged[name] = [];

    merged.fold_key = id;
    merged.entry_id = anchor.entry_id ?? null;
    merged.id = merged.id ?? anchor.entry_id ?? id;
    merged.kind = anchor.kind ?? null;
    merged.latest_entry_id = newest.entry_id ?? anchor.entry_id ?? null;
    merged.created_at = anchor.created_at || null;
    merged.updated_at = newest.created_at || anchor.created_at || null;
    merged.create_session_id = anchor.source_session_id || null;
    merged.last_session_id = newest.source_session_id || anchor.source_session_id || null;
    merged.source_session_id = merged.last_session_id;
    merged.log_card_id = anchor.log_card_id || null;
    merged.session_deleted = Boolean(anchor.session_deleted || newest.session_deleted);
    merged.has_session = Boolean(merged.source_session_id) && !merged.session_deleted;
    merged.event_count = live.length;
    out.push({ row: merged, seq: bucket.newest });
  }

  return out.sort((a, b) => b.seq - a.seq).map((v) => v.row);
}

/**
 * `group-into-one` — SESSION-BOUND many-to-one.
 *
 * The other four strategies collapse N rows to one ROW. This one collapses N
 * rows to one CARD THAT STILL OWNS ALL N: the group keeps `entries[]`, so a
 * card can say "· 3 results", a pending state can ask whether ANY of them is
 * updating, and a downstream filter can re-pick the representative inside the
 * filtered subset (see the `pickRepresentative` derive) — which is what lets a
 * Video tab show a different cover for the same session than the All tab.
 *
 * A row with no group key falls back to its OWN identity, prefixed `legacy:`
 * so it can never collide with a real group key.
 */
function foldGroupIntoOne(rows, foldSpec) {
  const key = foldSpec.key || 'source_session_id';
  const fallbackKey = foldSpec.fallback_key || 'entry_id';
  const rep = foldSpec.representative || {};
  const prefer = Array.isArray(rep.prefer_nonempty) ? rep.prefer_nonempty : [];

  const groups = new Map();
  for (const row of rows) {
    const raw = readKey(row, key);
    const id = raw == null || raw === ''
      ? `legacy:${readKey(row, fallbackKey) ?? row.entry_id ?? ''}`
      : String(raw);
    if (!groups.has(id)) groups.set(id, { key: id, session_id: raw == null || raw === '' ? null : raw, rows: [] });
    groups.get(id).rows.push(row);
  }

  const out = [];
  for (const group of groups.values()) {
    // newest-first inside the group: the card's cover is its most recent work
    // unless a `prefer_nonempty` field says otherwise.
    const ordered = [...group.rows].sort((a, b) => timeOf(b) - timeOf(a));
    out.push(buildGroup({ ...group, rows: ordered }, prefer));
  }
  return out.sort((a, b) => timeOf(b) - timeOf(a));
}

/**
 * One `group-into-one` item from its rows. Exported through `pickRepresentative`
 * so the SAME rule recomputes a group after a filter — the representative is a
 * function of the visible subset, never a value frozen at fold time.
 */
export function buildGroup(group, prefer = []) {
  const rows = group.rows || [];
  const representative = rows.find((r) => prefer.some((p) => {
    const v = getPath(r, p);
    return v != null && v !== '';
  })) || rows[0] || {};
  const newest = rows[0] || {};
  // Job-by-Contract: the CONTRACT anchor may not be the media-richest representative, but
  // only the anchor row carries the contract fields — backfill from whichever member anchors
  // the contract so the group row (and the detail `focusEntry` folded from it) surfaces the
  // lifecycle + progress regardless of which member represents the tile. Absent → nothing (BC).
  const contractAnchor = representative.contract_uid != null
    ? representative
    : (rows.find((r) => r && r.contract_uid != null) || {});
  return {
    ...representative,
    ...(representative.contract_uid == null && contractAnchor.contract_uid != null ? {
      contract_uid: contractAnchor.contract_uid,
      ...(contractAnchor.region != null ? { region: contractAnchor.region } : null),
      ...(contractAnchor.contract_lifecycle != null ? { contract_lifecycle: contractAnchor.contract_lifecycle } : null),
      ...(contractAnchor.contract_progress != null ? { contract_progress: contractAnchor.contract_progress } : null),
    } : null),
    group_key: group.key,
    grid_id: group.key,
    session_id: group.session_id ?? null,
    entries: rows,
    count: rows.length,
    entry_id: representative.entry_id ?? null,
    id: representative.id ?? representative.entry_id ?? group.key,
    representative_id: representative.entry_id ?? null,
    rep_created_at: representative.created_at || null,
    created_at: newest.created_at || representative.created_at || null,
  };
}

/**
 * `none` — no merge. Identity is the ROW (an agent that PATCHes the same row in
 * place has nothing to reduce), and `order` decides the sequence.
 */
function foldNone(rows, foldSpec) {
  const key = foldSpec.key || 'id';
  const fallbackKey = foldSpec.fallback_key || 'entry_id';
  const byId = new Map();
  for (const row of chronological(rows)) {
    const k = readKey(row, key) ?? readKey(row, fallbackKey) ?? row.entry_id;
    if (k == null) continue;
    byId.set(String(k), row);
  }
  const items = [...byId.values()];
  // Absent an explicit `order`, newest-first — the same default every other
  // strategy lands on.
  return foldSpec.order ? items : items.sort((a, b) => timeOf(b) - timeOf(a));
}

/**
 * `[]` when the fold block is honourable, or one `__unsupported` marker per
 * problem. Called by the context builder so an unknown strategy is REPORTED
 * (and degraded to the default) rather than silently rendering something else.
 */
export function validateFold(foldSpec) {
  const strategy = foldSpec?.strategy;
  if (strategy == null || FOLD_STRATEGIES.includes(String(strategy))) return [];
  return [{ __unsupported: 'fold strategy', name: String(strategy) }];
}

/**
 * Fold normalized rows to current items, per `data.fold.strategy`.
 *
 * An unknown strategy degrades to `event-sourced-latest` (and is reported by
 * `validateFold`): a blueprint written for a newer runtime still shows its
 * data, merged, rather than an empty page.
 */
export function foldRows(rows, foldSpec = {}) {
  const strategy = FOLD_STRATEGIES.includes(String(foldSpec.strategy))
    ? String(foldSpec.strategy)
    : DEFAULT_STRATEGY;

  let items;
  switch (strategy) {
    case 'field-merge':
      items = foldFieldMerge(rows, foldSpec);
      break;
    case 'group-into-one':
      items = foldGroupIntoOne(rows, foldSpec);
      break;
    case 'none':
      items = foldNone(rows, foldSpec);
      break;
    case 'latest-wins':
      items = foldLatest(rows, foldSpec, false);
      break;
    default:
      items = foldLatest(rows, foldSpec, true);
      break;
  }

  return dropSamples(applyOrder(items, foldSpec));
}
